import type { Immutable } from "immer";
import React from "react";
import { slugify } from "tsi-common-react/src/utils/text";
import { t } from "ttag";

import type { SpotlightModule as SpotlightModuleValue } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/components/pdp-spotlight.html`` (the tempurpedic-local PDP
 * ``SpotlightModule``, ``PDPModuleAssembly`` key ``spotlight``).
 *
 * The module wraps an ``OwnerSpotlight`` snippet chosen via
 * ``SnippetChooserBlock``. That snippet now implements
 * ``get_data_for_client_render`` + ``json_schema`` (see
 * ``server/cms/snippets.py``), so the serializer ships the typed
 * ``spotlight`` object below. Per-field render parity with the legacy template:
 *
 *   - ``spotlight_copy`` / ``owner_copy``: RichTextField rendered by legacy
 *     ``|safe`` => richtext-expanded HTML (server-side ``richtext()``), injected
 *     bare (no ``.rich-text`` wrapper -- these are values, not bound fields).
 *   - ``top_video``: a raw embed HTML string (legacy ``|safe``), injected bare
 *     into ``.owner-spotlight__wistia-container`` (MediaBlock precedent: the
 *     legacy template added NO Wistia re-init JS, so neither do we).
 *   - ``owner_title``: a raw HTML string (legacy ``|safe``), injected bare.
 *   - ``owner_name`` / ``cta_title``: plain text (React auto-escapes, matching
 *     ``{{ }}``).
 *   - ``top_image_url`` / ``bottom_image_url``: the raw ``.file.url`` used in the
 *     inline ``background-image`` (legacy used ``.file.url`` directly, no
 *     rendition).
 *
 * PHASE-2 FLAG: the legacy template's owner-name modifier
 * ``owner-spotlight__owner-name--{{ value.product.attr.brand_color }}`` read
 * ``value.product`` -- a variable that is NOT on the block value (the
 * ``SpotlightModule`` StructBlock only has ``spotlight``). It therefore always
 * resolved to EMPTY in legacy, yielding a trailing ``--`` with no suffix. That
 * empty behavior is reproduced verbatim here. The product (and thus
 * ``brand_color``) is page/configurator context the
 * ``PDPModuleAssembly`` map (#33589) supplies; wiring the real brand-color
 * suffix is deferred to Phase 2.
 */
export const SpotlightModule = (
    props: Immutable<{ value: SpotlightModuleValue }>,
): React.JSX.Element | null => {
    const spotlight = props.value.spotlight;
    if (!spotlight) {
        return null;
    }
    return (
        <div className="owner-spotlight l-capped-width u-margin-bottom--xl">
            <div className="owner-spotlight__title-container">
                <div className="owner-spotlight__title">
                    {t`Owner Spotlight`}
                </div>
                <div
                    className="owner-spotlight__title-copy"
                    dangerouslySetInnerHTML={{
                        __html: spotlight.spotlight_copy,
                    }}
                />
            </div>
            <div className="owner-spotlight__content u-flex-container">
                <div className="owner-spotlight__imagery">
                    {spotlight.top_video ? (
                        <div className="owner-spotlight__image-container owner-spotlight__image-container--top">
                            <EditorContent>
                                {(marker) => (
                                    <div
                                        className="owner-spotlight__wistia-container"
                                        dangerouslySetInnerHTML={{
                                            __html: spotlight.top_video,
                                        }}
                                        // The Wistia loader rewrites this container's
                                        // contents as soon as its external script
                                        // runs, which can beat hydration -- React
                                        // must not reconcile it.
                                        suppressHydrationWarning
                                        {...marker}
                                    />
                                )}
                            </EditorContent>
                        </div>
                    ) : (
                        <div
                            className="owner-spotlight__image-container owner-spotlight__image-container--top"
                            style={{
                                backgroundImage: `url(${spotlight.top_image_url})`,
                            }}
                        />
                    )}
                    <div
                        className="owner-spotlight__image-container owner-spotlight__image-container--bottom"
                        style={{
                            backgroundImage: `url(${spotlight.bottom_image_url})`,
                        }}
                    />
                </div>
                <div className="owner-spotlight__details">
                    {/* Empty ``--`` suffix matches legacy (product not on value;
                        see PHASE-2 FLAG above). */}
                    <div className="owner-spotlight__owner-name owner-spotlight__owner-name--">
                        {spotlight.owner_name}
                    </div>
                    <div
                        className="owner-spotlight__owner-title"
                        dangerouslySetInnerHTML={{
                            __html: spotlight.owner_title,
                        }}
                    />
                    <div className="owner-spotlight__bar" />
                    <div
                        className="owner-spotlight__owner-copy"
                        dangerouslySetInnerHTML={{
                            __html: spotlight.owner_copy,
                        }}
                    />
                    <a
                        href="#"
                        className={`button owner-spotlight__button al-owner-spotlight__button--${slugify(
                            spotlight.cta_title,
                        )}`}
                    >
                        {spotlight.cta_title}
                    </a>
                </div>
            </div>
        </div>
    );
};
